import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useRouteContext,
} from '@tanstack/react-router';
import { MenuProps } from 'antd';
import {
  Bell,
  Bot,
  Building2,
  CreditCard,
  LayoutGrid,
  List,
  Phone,
  Shield,
  ShieldUser,
  Sparkles,
  User,
  Users,
  IdCardLanyard,
  KeyRound,
} from 'lucide-react';
import { useMemo } from 'react';

import { usePermissions } from '../../../../modules/account/hooks';
import { SettingsLayout } from '../../../../ui/components/layout';

const settingsMenuLink = (to: string, text: string) => (
  <Link to={to} className="settings-layout-menu-item-link">
    {text}
  </Link>
);

function SettingsLayoutComponent() {
  const location = useLocation();

  const { account, selectedOrganization } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const { can } = usePermissions();
  const canViewSettings = can('settings', 'view');
  const canManageBilling = can('settings', 'manageBilling');
  const canManagePhone = can('phone', 'view');

  const menuItems: MenuProps['items'] = useMemo(() => {
    const userGroup: MenuProps['items'] = [
      {
        type: 'group',
        label: (
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {`${account.firstName} ${account.lastName}`}
          </span>
        ),
        children: [
          {
            key: '/settings/profile',
            label: settingsMenuLink('/settings/profile', 'Profile'),
            icon: <User size="1em" />,
          },
          {
            key: '/settings/security',
            label: settingsMenuLink('/settings/security', 'Security'),
            icon: <Shield size="1em" />,
          },
        ],
      },
    ];

    const membershipGroup: MenuProps['items'] = [
      {
        type: 'group',
        label: 'Workspace',
        children: [
          {
            key: '/settings/membership',
            label: settingsMenuLink('/settings/membership', 'Membership'),
            icon: <IdCardLanyard size="1em" />,
          },
          {
            key: '/settings/personal-ai-preferences',
            label: settingsMenuLink('/settings/personal-ai-preferences', 'Personal AI preferences'),
            icon: <Sparkles size="1em" />,
          },
          {
            key: '/settings/notifications',
            label: settingsMenuLink('/settings/notifications', 'Notifications'),
            icon: <Bell size="1em" />,
          },
          {
            key: '/settings/connected-apps',
            label: settingsMenuLink('/settings/connected-apps', 'Connected Apps'),
            icon: <LayoutGrid size="1em" />,
          },
          {
            // Distinct from Connected Apps above: that lists mailboxes K3X reaches
            // out to, this lists external clients granted access into K3X.
            key: '/settings/authorized-apps',
            label: settingsMenuLink('/settings/authorized-apps', 'Authorized Apps'),
            icon: <KeyRound size="1em" />,
          },
        ],
      },
    ];

    const organizationGroup: MenuProps['items'] = [
      {
        type: 'group',
        label: (
          <span
            style={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedOrganization.name ?? 'Company'}
          </span>
        ),
        children: [
          {
            key: '/settings/company',
            label: settingsMenuLink('/settings/company', 'Company'),
            icon: <Building2 size="1em" />,
          },
          {
            key: '/settings/team',
            label: settingsMenuLink('/settings/team', 'Team'),
            icon: <Users size="1em" />,
          },
          {
            key: '/settings/custom-fields',
            label: settingsMenuLink('/settings/custom-fields', 'Custom Fields'),
            icon: <List size="1em" />,
          },
          {
            key: '/settings/ai-behavior',
            label: settingsMenuLink('/settings/ai-behavior', 'AI Behavior'),
            icon: <Bot size="1em" />,
          },
          {
            key: '/settings/phone',
            label: settingsMenuLink('/settings/phone', 'Phone & Voicemail'),
            icon: <Phone size="1em" />,
          },
          {
            key: '/settings/roles',
            label: settingsMenuLink('/settings/roles', 'Roles & Permissions'),
            icon: <ShieldUser size="1em" />,
          },
          {
            key: '/settings/billing',
            label: settingsMenuLink('/settings/billing', 'Billing'),
            icon: <CreditCard size="1em" />,
          },
        ].filter((item) => {
          if (item.key === '/settings/phone') {
            return canManagePhone;
          }
          if (item.key === '/settings/billing') {
            return canManageBilling;
          }
          return true;
        }),
      },
    ];

    if (canViewSettings) {
      return [...userGroup, ...membershipGroup, ...organizationGroup];
    }
    return [...userGroup, ...membershipGroup];
  }, [
    account, 
    selectedOrganization, 
    canManagePhone, 
    canViewSettings, 
    canManageBilling,
  ]);

  const menuProps: Pick<MenuProps, 'items' | 'selectedKeys'> = useMemo(
    () => ({
      items: menuItems,
      selectedKeys: [location.pathname],
    }),
    [menuItems, location.pathname],
  );

  return (
    <SettingsLayout menuProps={menuProps}>
      <Outlet />
    </SettingsLayout>
  );
}

export const Route = createFileRoute('/_authenticated/_app/settings')({
  component: SettingsLayoutComponent,
});
